import React, { useMemo, useState, useEffect } from "react"
import { MDBBtn, MDBBtnGroup, MDBTooltip, MDBIcon } from "mdb-react-ui-kit";
import roundSelectorStyles from './RoundSelector.module.scss';

export default function RoundSelector({torneo, onRoundChange, roundNumSelected}) {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [visibleRounds, setVisibleRounds] = useState({ start: 1, end: 8 });

  // Check screen size and update responsive state
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 576);
      setIsTablet(width >= 576 && width < 992);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Update visible rounds based on screen size and selected round
  useEffect(() => {
    if (!torneo || !torneo.roundResults) return;

    // Filtrar solo rondas con contenido (ignorar rondas vacías)
    // const totalRounds = torneo.roundResults.filter(r => r && r.length > 0).length;
    const totalRounds = torneo.roundResults.length;
    let roundsPerView = 8; // Default for desktop

    if (isMobile) {
      roundsPerView = 4;
    } else if (isTablet) {
      roundsPerView = 6;
    }

    // Center the visible range around the selected round
    let start = Math.max(1, roundNumSelected - Math.floor(roundsPerView / 2));
    let end = Math.min(totalRounds, start + roundsPerView - 1);
    
    // Adjust start if we're at the end
    if (end - start + 1 < roundsPerView && start > 1) {
      start = Math.max(1, end - roundsPerView + 1);
    }

    setVisibleRounds({ start, end });
  }, [roundNumSelected, torneo, isMobile, isTablet]);

  const onNumberClick = num => {
    onRoundChange(num);
  }

  // Calcular cuántas rondas están pre-calculadas
  const getNumPreCalculatedRounds = () => {
    if (torneo.championType === 'EliminatoriaNoCondicionada' && torneo.numZeros) {
      return torneo.numZeros;
    }
    return 0;
  };

  // Calculate round statistics
  const roundStats = useMemo(() => {
    if (!torneo || !torneo.roundResults) return {};
    
    const numPreCalculated = getNumPreCalculatedRounds();
    
    const stats = {};
    torneo.roundResults.forEach((round, index) => {
      // Ignorar rondas vacías
      if (!round || round.length === 0) {
        return;
      }
      
      const roundNumber = index + 1;
      const totalMatches = round.length;
      
      const completedMatches = round.filter(match => {
        if (!match || !match.ids) return false;
        
        // Para partidos exentos (un solo equipo), verificar si tienen puntos asignados
        if (match.ids.length === 1) {
          return match.points && 
                 match.points[0] !== '' && 
                 match.points[1] !== '' &&
                 parseInt(match.points[0]) === torneo.numJuegos && 
                 parseInt(match.points[1]) === 0;
        }
        
        // Para partidos normales (dos equipos)
        return match.points && match.points[0] !== '' && match.points[1] !== '' && 
               (parseInt(match.points[0]) > 0 || parseInt(match.points[1]) > 0);
      }).length;
      
      // Una ronda está pre-calculada si está dentro del rango de pre-cálculo
      const isPreCalculated = numPreCalculated > 0 && roundNumber <= numPreCalculated && roundNumber > torneo.roundNum;
      const isAccessible = roundNumber <= torneo.roundNum || isPreCalculated;
      
      stats[roundNumber] = {
        total: totalMatches,
        completed: completedMatches,
        isComplete: completedMatches === totalMatches,
        isAccessible: isAccessible,
        isPreCalculated: isPreCalculated
      };
    });
    
    return stats;
  }, [torneo]);

  const getRoundStatus = (roundNum) => {
    const stats = roundStats[roundNum];
    if (!stats) return 'future';
    
    if (!stats.isAccessible) return 'future';
    if (stats.isComplete) return 'completed';
    if (roundNum === torneo.roundNum) return 'current';
    if (stats.isPreCalculated) return 'precalculated';
    return 'accessible';
  };

  const getRoundTooltip = (roundNum) => {
    const stats = roundStats[roundNum];
    if (!stats) return `Ronda ${roundNum} - No disponible`;
    
    const status = getRoundStatus(roundNum);
    let statusText = '';
    
    switch (status) {
      case 'completed':
        statusText = 'Completada';
        break;
      case 'current':
        statusText = 'Actual';
        break;
      case 'accessible':
        statusText = 'Accesible';
        break;
      case 'precalculated':
        statusText = 'Pre-calculada';
        break;
      case 'future':
        statusText = 'Futura';
        break;
    }
    
    return `Ronda ${roundNum} - ${statusText} (${stats.completed}/${stats.total} enfrentamientos)`;
  };

  const renderRounds = (firstRound, lastRound, roundNum, roundNumSelected) => {
    let nums = []
    for (let i = firstRound; i <= lastRound; i++) {
      nums.push(
        <RoundNumber
          key={i}
          num={i}
          active={roundNumSelected === i}
          onNumberClick={onNumberClick}
          roundNum={roundNum}
          status={getRoundStatus(i)}
          tooltip={getRoundTooltip(i)}
          stats={roundStats[i]}
          isMobile={isMobile}
        />
      )
    }
    return nums
  }

  const handlePrevious = () => {
    if (roundNumSelected > 1) {
      onNumberClick(roundNumSelected - 1);
    }
  };

  const handleNext = () => {
    // Contar solo rondas con contenido
    const maxRound = torneo.roundResults ? torneo.roundResults.filter(r => r && r.length > 0).length : torneo.roundNum;
    if (roundNumSelected < maxRound) {
      onNumberClick(roundNumSelected + 1);
    }
  };

  const handlePreviousPage = () => {
    const newStart = Math.max(1, visibleRounds.start - (visibleRounds.end - visibleRounds.start + 1));
    const roundsPerView = visibleRounds.end - visibleRounds.start + 1;
    const totalRounds = torneo.roundResults.filter(r => r && r.length > 0).length;
    const newEnd = Math.min(totalRounds, newStart + roundsPerView - 1);
    setVisibleRounds({ start: newStart, end: newEnd });
  };

  const handleNextPage = () => {
    const roundsPerView = visibleRounds.end - visibleRounds.start + 1;
    const totalRounds = torneo.roundResults.filter(r => r && r.length > 0).length;
    const newStart = Math.min(totalRounds - roundsPerView + 1, visibleRounds.end + 1);
    const newEnd = Math.min(totalRounds, newStart + roundsPerView - 1);
    setVisibleRounds({ start: newStart, end: newEnd });
  };

  const canGoPreviousPage = visibleRounds.start > 1;
  const canGoNextPage = visibleRounds.end < (torneo.roundResults ? torneo.roundResults.filter(r => r && r.length > 0).length : 0);

  return (
    torneo && torneo.roundResults && (
      <div className={roundSelectorStyles.roundSelectorContainer}>
        {/* Single row layout with all controls */}
        <div className={roundSelectorStyles.singleRowLayout}>
          {/* Page navigation for mobile/tablet */}
          <MDBTooltip tag='span' title='Rondas anteriores' placement="top">
            <MDBBtn 
              size="sm" 
              color="primary"
              outline
              onClick={handlePreviousPage}
              disabled={!canGoPreviousPage}
              className={roundSelectorStyles.pageButton}
            >
              <MDBIcon fas icon="chevron-left" />
              <MDBIcon fas icon="chevron-left" />
            </MDBBtn>
          </MDBTooltip>

          {/* Single round navigation */}
          <MDBTooltip tag='span' title='Ronda anterior' placement="top">
            <MDBBtn 
              size="sm" 
              color="primary"
              onClick={handlePrevious}
              disabled={roundNumSelected <= 1}
              className={roundSelectorStyles.navButton}
            >
              <MDBIcon fas icon="chevron-left" />
            </MDBBtn>
          </MDBTooltip>

          {/* Rounds label */}
          {!isMobile && (
            <div className={roundSelectorStyles.roundsLabel}>
              <b>Rondas: </b>
            </div>
          )}

          {/* Page info for mobile/tablet */}
          {(isMobile || isTablet) && (
            <span className={roundSelectorStyles.pageInfo}>
              {visibleRounds.start}-{visibleRounds.end}/{torneo.roundResults.filter(r => r && r.length > 0).length}
            </span>
          )}

          {/* Rounds list */}
          <div className={roundSelectorStyles.roundsList}>
            {renderRounds(visibleRounds.start, visibleRounds.end, torneo.roundNum, roundNumSelected)}
          </div>

          {/* Single round navigation */}
          <MDBTooltip tag='span' title='Siguiente ronda' placement="top">
            <MDBBtn 
              size="sm" 
              color="primary"
              onClick={handleNext}
              disabled={roundNumSelected >= (torneo.roundResults ? torneo.roundResults.filter(r => r && r.length > 0).length : torneo.roundNum)}
              className={roundSelectorStyles.navButton}
            >
              <MDBIcon fas icon="chevron-right" />
            </MDBBtn>
          </MDBTooltip>

          {/* Page navigation for mobile/tablet */}
          <MDBTooltip tag='span' title='Siguientes rondas' placement="top">
            <MDBBtn 
              size="sm" 
              color="primary"
              outline
              onClick={handleNextPage}
              disabled={!canGoNextPage}
              className={roundSelectorStyles.pageButton}
            >
              <MDBIcon fas icon="chevron-right" />
              <MDBIcon fas icon="chevron-right" />
            </MDBBtn>
          </MDBTooltip>

          {/* Round info - compact version */}
          {roundStats[roundNumSelected] && (
            <div className={roundSelectorStyles.roundInfoCompact}>
              <small className="text-muted">
                ({roundStats[roundNumSelected].completed}/{roundStats[roundNumSelected].total})
                {getRoundStatus(roundNumSelected) === 'completed' && (
                  <MDBIcon fas icon="check-circle" className="text-success ms-1" size="sm" />
                )}
              </small>
            </div>
          )}
        </div>
      </div>
    )
  )
}

const RoundNumber = ({ num, active, onNumberClick, roundNum, status, tooltip, stats, isMobile }) => {
  const getButtonClass = () => {
    switch (status) {
      case 'completed':
        return active ? roundSelectorStyles.activeCompleted : roundSelectorStyles.completed;
      case 'current':
        return active ? roundSelectorStyles.activeCurrent : roundSelectorStyles.current;
      case 'accessible':
        return active ? roundSelectorStyles.activeAccessible : roundSelectorStyles.accessible;
      case 'precalculated':
        return active ? roundSelectorStyles.activePrecalculated : roundSelectorStyles.precalculated;
      case 'future':
      default:
        return roundSelectorStyles.disabled;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <MDBIcon fas icon="check" size="sm" className="ms-1" />;
      case 'current':
        return <MDBIcon fas icon="play" size="sm" className="ms-1" />;
      case 'precalculated':
        return <MDBIcon fas icon="calendar-alt" size="sm" className="ms-1" />;
      default:
        return null;
    }
  };

  return (
    <MDBTooltip tag='span' title={tooltip} placement="top">
      <MDBBtnGroup className={`me-2 ${getButtonClass()}`} role="group" aria-label={`Round ${num}`}>
        <MDBBtn 
          size="sm"
          color={active ? "primary" : "light"}
          onClick={() => onNumberClick(num)} 
          disabled={status === 'future'}
          className={roundSelectorStyles.roundButton}
        >
          {num}
          {getStatusIcon()}
        </MDBBtn>
      </MDBBtnGroup>
    </MDBTooltip>
  );
};
